/**
 * @file writeoff-columns.tsx
 * @description Колонки реестра списаний для `ResponsiveList` (phase19.31,
 * зона «Склад»; разбор B — «Списания»).
 *
 * Состав прежний (phase19.23 A2 — «сколько и кем списано видно из списка»):
 * Номер · Склад · Причина · Статус · Создано (дата и кто) · Позиций · Кол-во.
 * Номер — в одну строку. Список до 1024: «WO-… ● Проведено / Склад ·
 * причина / 2 позиции · 5 шт · 26.09.2026».
 */

import { CreatedCell, DocumentNumber, NumberCell } from "./document-cells";
import { positionsCount, quantityTotal, totalsLine } from "./document-totals";
import type { ListColumn } from "../../ui/responsive-list-types";
import { StatusPill } from "../../ui/StatusPill";
import { WRITEOFF_REASON_LABEL, type WriteoffDto } from "../../../lib/api-wms";
import { WMS_DOCUMENT_STATUS_META } from "../../../lib/shop/status-meta";
import { formatShopDate } from "../../../lib/shop-time";

type Writeoff = WriteoffDto;

export const WRITEOFF_COLUMNS: ListColumn<Writeoff>[] = [
  {
    key: "number",
    header: "Номер",
    width: "w-[168px]",
    cell: (w) => <DocumentNumber value={w.writeoffNumber} />,
    compactCell: (w) => <span className="font-mono">{w.writeoffNumber}</span>,
    compact: "title",
  },
  {
    key: "warehouse",
    header: "Склад",
    width: "w-[160px]",
    cell: (w) => w.warehouse?.name ?? "—",
    compact: "subtitle",
    fold: { into: "number" },
  },
  { key: "reason", header: "Причина", cell: (w) => WRITEOFF_REASON_LABEL[w.reason], compact: "subtitle" },
  {
    key: "status",
    header: "Статус",
    width: "w-[132px]",
    cell: (w) => <StatusPill meta={WMS_DOCUMENT_STATUS_META[w.status]} />,
    compact: "trailing",
  },
  {
    key: "created",
    header: "Создано",
    width: "w-[148px]",
    cell: (w) => <CreatedCell at={w.createdAt} by={w.createdBy?.displayName} />,
    fold: { into: "status", cell: (w) => formatShopDate(w.createdAt) },
  },
  {
    key: "positions",
    header: "Позиций",
    width: "w-[88px]",
    align: "end",
    cell: (w) => <NumberCell value={positionsCount(w.items)} />,
    compact: "meta",
    compactCell: (w) => totalsLine(w.items),
  },
  {
    key: "quantity",
    header: "Кол-во",
    width: "w-[88px]",
    align: "end",
    cell: (w) => <NumberCell value={quantityTotal(w.items)} />,
    fold: { into: "positions", cell: (w) => `${quantityTotal(w.items)} шт` },
  },
  { key: "date", header: "Дата", compactOnly: true, cell: (w) => formatShopDate(w.createdAt), compact: "meta" },
];
